<template>
  <div class="card collection-table-wrapper">
    <div class="table-responsive mb-0">
      <table
        class="table card-table table-vcenter"
        data-test="template-selection-table"
      >
        <thead>
          <tr>
            <th>Template</th>
            <th>In rotation</th>
          </tr>
        </thead>
        <template v-if="groupSelected">
          <tbody
            v-if="isLoading"
            role="rowgroup"
          >
            <tr
              role="row"
              class="b-table-busy-slot"
            >
              <td colspan="2">
                <div
                  class="p-5"
                  data-test="dashboard-loading"
                >
                  <div class="loader mx-auto" />
                </div>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr
              v-for="(item, index) in templateList"
              :key="`include-${index}`"
            >
              <td>
                <div>
                  <a
                    :href="`/templates/update/${item.id}`"
                    target="_blank"
                  >
                    {{ item.name }}
                  </a>
                </div>
                <div class="small text-muted">
                  {{ `${item.width} x ${item.height}` }}
                </div>
              </td>
              <td class="w-1">
                <label
                  class="d-flex flex-column align-self-center m-0 ml-5"
                  :id="`template-rotation-${item.id}`"
                >
                  <input
                    type="checkbox"
                    :name="`template-checkbox${item.id}`"
                    class="custom-switch-input"
                    :id="item.id"
                    data-test="template-checkbox"
                    v-model="selected"
                    :value="item.id"
                    :disabled="
                      disabled || (selected.length < 2 && selected.indexOf(item.id) === 0)
                    "
                    @change="toggleClicked"
                  >
                  <span class="custom-switch-indicator" />
                </label>
              </td>
            </tr>
          </tbody>
        </template>
      </table>
    </div>
    <div
      v-if="!groupSelected"
      class="text-center p-5"
    >
      Please select a template group
    </div>
  </div>
</template>
<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import * as types from '@/modules/templates/store/types';
import * as contentTypes from '@/modules/content/store/types';

import helper from '@/core/helper';

export default {
  name: 'TemplateCollectionTable',
  props: {
    groupSelected: {
      type: Boolean,
      required: true,
    },
    selectedGroupId: {
      type: Number,
      default: null,
    },
    groupIdChanged: {
      type: Boolean,
      default: false,
    },
    isNewCampaign: {
      type: Boolean,
      default: false,
    },
    templateList: {
      type: Array,
      default() {
        return [];
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selected: [],
      isLoading: false,
    };
  },
  computed: {
    ...mapState('content', ['creatives']),
    ...mapState('campaigns', ['campaign', 'order', 'campaignFeedId']),
    ...mapState('templates', ['availableTemplates', 'selectedTemplates']),
  },
  methods: {
    ...mapActions('content', [
      contentTypes.FETCH_CREATIVE_TEMPLATE_BY_ID,
      contentTypes.PUT_CREATIVES,
    ]),
    ...mapActions('templates', [
      types.SET_SELECTED_TEMPLATES,
    ]),
    ...mapMutations('templates', [types.UPDATE_AVAILABLE_TEMPLATES]),
    getTemplateEditableProperties(id) {
      const tempProperties = this.templateList.find(
        (item) => item.id === parseInt(id, 10),
      );
      return tempProperties ? tempProperties.properties : [];
    },
    getTemplatesInGroup: helper.debounce(function getTemplatesInGroup() {
      this.isLoading = true;
      if (!this.selectedGroupId) {
        this.isLoading = false;
        return;
      }
      if (this.creatives.length > 0 && !this.groupIdChanged) {
        this.selected = this.creatives
          .filter((item) => item.paused === false)
          .map((item) => item.creativeTemplateId);
      } else {
        this.selected = this.templateList.map((item) => item.id);
      }
      this.isLoading = false;
    }, 500),
    updateTemplatesSelected() {
      this.SET_SELECTED_TEMPLATES(this.selected);
    },
    buildCreativeRequestList() {
      return this.creatives
        .map((creative) => ({
          creativeTemplateId: creative.creativeTemplateId,
          isPaused: creative.paused,
          properties: creative.properties,
        }));
    },
    toggleClicked($event) {
      if (this.campaign.state === 'PAUSED' || this.campaign.state === 'LIVE') {
        this.warnUserOfChangingCreativeState()
          .then(() => {
            const creativeToUpdate = this.creatives.find((cr) => cr.creativeTemplateId === parseInt($event.target.value, 10));
            if (creativeToUpdate) {
              creativeToUpdate.paused = !$event.target.checked;
            }
            const payload = {
              body: {
                campaignId: this.campaign.id,
                feedId: this.campaignFeedId,
                orderId: this.order.id,
                creatives: this.buildCreativeRequestList(),
              },
            };
            this.PUT_CREATIVES(payload);
          })
          .catch(() => {
            $event.target.checked === true
              ? ($event.target.checked = false)
              : ($event.target.checked = true);
          });
      }
    },
    warnUserOfChangingCreativeState() {
      return new Promise((resolve, reject) => {
        this.$bvModal
          .msgBoxConfirm(
            'Are you sure you want to change the state of this creative',
            {
              title: 'Warning',
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'danger',
              okTitle: 'Yes',
              cancelTitle: 'No',
              footerClass: 'p-2',
              hideHeaderClose: false,
              centered: true,
            },
          )
          .then((value) => {
            if (!value) return reject();
            return resolve();
          })
          .catch(() => reject());
      });
    },
  },
  created() {
    this.getTemplatesInGroup();
  },
  watch: {
    groupSelected() {
      this.getTemplatesInGroup();
    },
    selectedGroupId() {
      this.getTemplatesInGroup();
    },
    selected() {
      this.updateTemplatesSelected();
    },
  },
};
</script>
<style scoped>
.collection-table-wrapper {
  max-height: 17rem;
}
.custom-switch-input:disabled ~ .custom-switch-indicator {
  cursor: no-drop;
}
</style>
